import { FieldObjectType } from 'module/api/field/types';
import { SourceType } from 'module/api/filter/types';
import { useTranslate } from 'module/hooks/use-translation';
import { FieldType } from 'module/types/filter-field/field/field';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { useMemo } from 'react';
import { useChampionsField } from './champions';
import { useCustomerMembersField } from './customer-members';

const FIELD_ID_COMPANY_NAME = -1964;
const FIELD_ID_CUSTOMER_MEMBERS = FIELD_ID_COMPANY_NAME - 1;
const FIELD_ID_CHAMPIONS = FIELD_ID_CUSTOMER_MEMBERS - 1;
const FIELD_ID_OVERALL_PROJECT_RATING = FIELD_ID_CHAMPIONS - 1;
const FIELD_ID_LAST_MILESTONE_RATING = FIELD_ID_OVERALL_PROJECT_RATING - 1;
const FIELD_ID_TRACKED_HOURS = FIELD_ID_LAST_MILESTONE_RATING - 1;
const FIELD_ID_BILLABLE_HOURS = FIELD_ID_TRACKED_HOURS - 1;
const FIELD_ID_TOTAL_ALLOCATED_HOURS = FIELD_ID_BILLABLE_HOURS - 1;
const FIELD_ID_ACTUAL_REVENUE = FIELD_ID_TOTAL_ALLOCATED_HOURS - 1;
const FIELD_ID_ACTUAL_PROFIT = FIELD_ID_ACTUAL_REVENUE - 1;
const FIELD_ID_ACTUAL_COST = FIELD_ID_ACTUAL_PROFIT - 1;
const FIELD_ID_ACTUAL_PROFIT_MARGIN = FIELD_ID_ACTUAL_COST - 1;
const FIELD_ID_ESTIMATED_REVENUE = FIELD_ID_ACTUAL_PROFIT_MARGIN - 1;
const FIELD_ID_ESTIMATED_PROFIT = FIELD_ID_ESTIMATED_REVENUE - 1;
const FIELD_ID_ESTIMATED_COST = FIELD_ID_ESTIMATED_PROFIT - 1;
const FIELD_ID_ESTIMATED_PROFIT_MARGIN = FIELD_ID_ESTIMATED_COST - 1;
const FIELD_ID_JOINED_AT = FIELD_ID_ESTIMATED_PROFIT_MARGIN - 1;
const FIELD_ID_LAST_ACTIVITY = FIELD_ID_JOINED_AT - 1;
const FIELD_ID_RECENT_RISK_SIGNALS = FIELD_ID_LAST_ACTIVITY - 1;
const FIELD_ID_RECENT_OPPORTUNITY_SIGNALS = FIELD_ID_RECENT_RISK_SIGNALS - 1;
const FIELD_ID_RECENT_OPERATIONAL_SIGNALS =
  FIELD_ID_RECENT_OPPORTUNITY_SIGNALS - 1;

export const useCompanyNativeFieldsAsFilterableFields = () => {
  const translate = useTranslate();
  const companyFields = useMemo(
    () =>
      [
        {
          fieldName: 'companyName',
          fieldLabel: translate('company.fields.company_name'),
          type: FieldType.Text,
          fieldId: FIELD_ID_COMPANY_NAME,
        },
        {
          fieldName: 'csatRating',
          fieldLabel: translate('company.fields.overall_project_rating'),
          type: FieldType.Number,
          fieldId: FIELD_ID_OVERALL_PROJECT_RATING,
          metaData: {
            ratingScale: 5,
          },
        },
        {
          fieldName: 'lastMilestoneCSAT',
          fieldLabel: translate('company.fields.last_milestone_rating'),
          type: FieldType.Number,
          fieldId: FIELD_ID_LAST_MILESTONE_RATING,
          metaData: {
            ratingScale: 5,
          },
        },
        {
          fieldName: 'trackedMinutes',
          fieldLabel: translate('company.fields.tracked_hours'),
          type: FieldType.Number,
          fieldId: FIELD_ID_TRACKED_HOURS,
          sourceType: SourceType.StarMeta,
        },
        {
          fieldName: 'billableMinutes',
          fieldLabel: translate('company.fields.billable_hours'),
          type: FieldType.Number,
          fieldId: FIELD_ID_BILLABLE_HOURS,
          sourceType: SourceType.StarMeta,
        },
        {
          fieldName: 'allocatedMins',
          fieldLabel: translate('company.fields.allocated_hours'),
          type: FieldType.Number,
          fieldId: FIELD_ID_TOTAL_ALLOCATED_HOURS,
          sourceType: SourceType.StarMeta,
        },
        {
          fieldName: 'actualRevenue',
          fieldLabel: translate('company.fields.revenue'),
          type: FieldType.Number,
          fieldId: FIELD_ID_ACTUAL_REVENUE,
          sourceType: SourceType.StarMeta,
        },
        {
          fieldName: 'actualProfit',
          fieldLabel: translate('company.fields.profit'),
          type: FieldType.Number,
          fieldId: FIELD_ID_ACTUAL_PROFIT,
          sourceType: SourceType.StarMeta,
        },
        {
          fieldName: 'actualCost',
          fieldLabel: translate('company.fields.cost'),
          type: FieldType.Number,
          fieldId: FIELD_ID_ACTUAL_COST,
          sourceType: SourceType.StarMeta,
        },
        {
          fieldName: 'actualMargin',
          fieldLabel: translate('company.fields.margin'),
          type: FieldType.Number,
          fieldId: FIELD_ID_ACTUAL_PROFIT_MARGIN,
          sourceType: SourceType.StarMeta,
        },
        {
          fieldName: 'estimatedRevenue',
          fieldLabel: translate('company.fields.forecasted_revenue'),
          type: FieldType.Number,
          fieldId: FIELD_ID_ESTIMATED_REVENUE,
          sourceType: SourceType.StarMeta,
        },
        {
          fieldName: 'estimatedProfit',
          fieldLabel: translate('company.fields.forecasted_profit'),
          type: FieldType.Number,
          fieldId: FIELD_ID_ESTIMATED_PROFIT,
          sourceType: SourceType.StarMeta,
        },
        {
          fieldName: 'estimatedCost',
          fieldLabel: translate('company.fields.forecasted_cost'),
          type: FieldType.Number,
          fieldId: FIELD_ID_ESTIMATED_COST,
          sourceType: SourceType.StarMeta,
        },
        {
          fieldName: 'estimatedMargin',
          fieldLabel: translate('company.fields.forecasted_margin'),
          type: FieldType.Number,
          fieldId: FIELD_ID_ESTIMATED_PROFIT_MARGIN,
          sourceType: SourceType.StarMeta,
        },
        {
          fieldName: 'lastActivityDate',
          fieldLabel: translate('company.fields.last_activity'),
          type: FieldType.Date,
          fieldId: FIELD_ID_LAST_ACTIVITY,
        },
        {
          fieldName: 'recentRiskSignals',
          fieldLabel: 'Recent Risks',
          type: FieldType.Number,
          fieldId: FIELD_ID_RECENT_RISK_SIGNALS,
        },
        {
          fieldName: 'recentOpportunitySignals',
          fieldLabel: 'Recent Opportunities',
          type: FieldType.Number,
          fieldId: FIELD_ID_RECENT_OPPORTUNITY_SIGNALS,
        },
        {
          fieldName: 'recentOperationalSignals',
          fieldLabel: 'Recent Operational',
          type: FieldType.Number,
          fieldId: FIELD_ID_RECENT_OPERATIONAL_SIGNALS,
        },
      ].map(
        (x) =>
          ({
            ...x,
            customField: false,
            private: false,
            sourceType:
              x.sourceType != null ? x.sourceType : SourceType.Company,
            objectType: FieldObjectType.Company,
          }) as FilterableField,
      ),
    [translate],
  );
  const { field: customerMembersField, isLoading: customerMembersLoading } =
    useCustomerMembersField(FIELD_ID_CUSTOMER_MEMBERS);
  const { field: championsField, isLoading: championsLoading } =
    useChampionsField(FIELD_ID_CHAMPIONS);

  return useMemo(
    () => ({
      fields: companyFields.concat([
        // accountOwnerField,
        customerMembersField,
        championsField,
      ]),
      loading: customerMembersLoading || championsLoading,
    }),
    [
      championsField,
      championsLoading,
      companyFields,
      customerMembersField,
      customerMembersLoading,
    ],
  );
};
